import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api from '../api/axios';
import { socket, updateSocketAuth } from '../api/socket'; // 👈 IMPORTED updateSocketAuth

/**
 * 👤 USER INTERFACE
 */
interface User {
  id: string;
  fullName: string;
  email: string;
  role: 'STUDENT' | 'TUTOR' | 'ADMIN';
  isActive: boolean;
  isVerified: boolean;
  walletBalance: number;
  phoneNumber?: string; 
  gender?: string;
  gradeLevel?: string;
  university?: string;
  major?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (identifier: string, password: string) => Promise<User>;
  logout: () => void;
  updateBalance: (newBalance: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * 🔄 AUTH CHECK (On Mount)
   */
  useEffect(() => {
    const checkUser = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const res = await api.get('/auth/profile');
          setUser(res.data);
          
          // 🛡️ SECURITY: Re-authenticate the socket using the valid token from storage
          updateSocketAuth(token);
          
        } catch (err) {
          console.error("Session expired:", err);
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
          setUser(null);
          
          // 🛡️ SECURITY: Clear the socket connection if token is invalid
          updateSocketAuth(null);
        }
      } else {
        // 🛡️ SECURITY: Ensure no lingering connection if no token exists
        updateSocketAuth(null);
      }
      setLoading(false);
    };
    checkUser();
  }, []);

  /**
   * 💰 REAL-TIME BILLING SYNC
   * Listens for the "Billing Pulse" from the backend.
   */
  useEffect(() => {
    if (!user) return;

    const handleBalanceUpdate = (data: { newBalance: number }) => {
      console.log("💸 Billing Pulse received. New Balance:", data.newBalance);
      updateBalance(data.newBalance);
    };

    socket.on('balance-updated', handleBalanceUpdate);

    return () => {
      socket.off('balance-updated', handleBalanceUpdate);
    };
  }, [user]); 

  /**
   * 🔑 LOGIN ACTION
   */
  const login = async (identifier: string, password: string): Promise<User> => {
    try {
      const res = await api.post('/auth/login', { identifier, password });
      const { token, user: userData } = res.data;

      localStorage.setItem('token', token);
      localStorage.setItem('userId', userData.id); 
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // 🛡️ SECURITY: Instantly securely connect the WebSocket with the new token
      updateSocketAuth(token);
      
      setUser(userData);
      return userData;
    } catch (err: any) {
      throw new Error(err.response?.data?.message || "Login failed");
    }
  };

  /**
   * 🚪 LOGOUT ACTION
   */
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    
    // 🛡️ SECURITY: Instantly disconnect the secure WebSocket
    updateSocketAuth(null);
    
    window.location.href = '/login';
  }, []);

  /**
   * 💰 UPDATE BALANCE HELPER
   */
  const updateBalance = (newBalance: number) => {
    setUser((prev) => (prev ? { ...prev, walletBalance: newBalance } : null));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateBalance }}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};